'use client'

import { LoadingSpinner } from '@/app/components/ui/feedback/loading-spinner'
import { CaptchaProvider } from '@/hooks/use-captcha'
import { useCart } from '@/hooks/use-cart'
import type { Cart } from '@/types/commerce-types'
import { ShoppingCart } from 'lucide-react'
import Link from 'next/link'
import { Suspense, useEffect } from 'react'
import CheckoutForm from '../../_components/forms/checkout-form'
import CheckoutDetailsPanel from '../../_components/panels/payment-details-panel'

const CheckoutPageContent = () => {
  const { isLoading, refetch, cart, cartKey } = useCart()
  // Use items directly from cart (React Query) to avoid race condition with Zustand store sync
  const items = cart?.items

  // Refetch cart data on mount only
  useEffect(() => {
    // Only refetch if we have a cart key
    if (cartKey) {
      refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartKey])

  // Show loading state only while actively loading
  // Don't block on cart.id as it could be 0 for new carts
  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="md" text="Loading cart..." />
      </div>
    )
  }

  // Handle missing cart after loading complete
  if (!cart) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="max-w-md text-center">
          <ShoppingCart className="mx-auto mb-6 h-16 w-16 text-gray-300" />
          <h2 className="mb-4 text-2xl font-bold text-gray-900">Unable to load cart</h2>
          <p className="mb-8 text-gray-600">
            There was a problem loading your cart. Please try refreshing the page or start shopping again.
          </p>
          <Link
            href="/"
            className="inline-flex items-center rounded-md bg-primary px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    )
  }

  // Show empty cart state
  if (!items || items.length === 0) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="max-w-md text-center">
          <ShoppingCart className="mx-auto mb-6 h-16 w-16 text-gray-300" />
          <h2 className="mb-4 text-2xl font-bold text-gray-900">Your cart is empty</h2>
          <p className="mb-8 text-gray-600">
            It looks like you haven't added any items to your cart yet. Start shopping to add items to your cart.
          </p>
          <Link
            href="/"
            className="inline-flex items-center rounded-md bg-primary px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    )
  }

  // Show checkout form when cart has items
  // At this point, TypeScript should know cart is defined, but we'll help it
  const validCart = cart as Cart

  return (
    <div className="relative flex-1">
      {/* Background color split screen for large screens */}
      <div className="absolute left-0 top-0 hidden h-full w-1/2 lg:block" aria-hidden="true" />
      <div className="absolute right-0 top-0 hidden h-full w-1/2 bg-slate-600 lg:block" aria-hidden="true" />

      <main className="relative">
        <h1 className="sr-only">Checkout</h1>
        <div className="mx-auto flex max-w-7xl flex-col-reverse lg:grid lg:grid-cols-2 lg:gap-16">
          <section aria-labelledby="payment-heading" className="py-2 pb-8 lg:py-6">
            <CheckoutForm cart={validCart} />
          </section>
          <section aria-labelledby="summary-heading" className="bg-slate-600 py-4 text-white lg:py-8">
            <CheckoutDetailsPanel cart={validCart} />
          </section>
        </div>
      </main>
    </div>
  )
}

const CheckoutPage = () => {
  return (
    <CaptchaProvider>
      <Suspense
        fallback={
          <div className="flex min-h-[60vh] items-center justify-center">
            <LoadingSpinner size="md" text="Loading checkout..." />
          </div>
        }
      >
        <CheckoutPageContent />
      </Suspense>
    </CaptchaProvider>
  )
}

export default CheckoutPage
